import { generatePath } from 'react-router-dom';
import { Endpoint } from '@/Config/api/endpoint';
import { httpClient } from '@/Config/api/httpClient.config';
import { DeepPartial } from '@/Config/types/DeepPartial';
import { ConversationDto } from '@/Domain/api/Conversation/dto/Conversation.dto';
import { Message } from '@/Domain/api/Conversation/dto/Message.dto';
import { RatingDto } from '@/Domain/api/Conversation/dto/Rating.dto';
import {
  WhyAISaidThatDto,
  WhyAISaidThatScheduledDto,
} from '@/Domain/api/Conversation/dto/WhyAISaidThat.dto';
import { ConversationTranslateRequest } from '@/Domain/api/Conversation/request/ConversationTranslate.request';
import { ListRequest } from '@/Domain/api/Conversation/request/List.request';
import { UpdateVisitorRequest } from '@/Domain/api/Conversation/request/UpdateVisitor.request';
import { Paginated } from '@/Domain/api/Shared/dto/Paginated.dto';
import { ApiMapper } from '@/Domain/mapper/App/Api.mapper';

/**
 * generatePath remove / at the end - we need to keep in die to old version of endpoint
 */
export class ConversationApi {
  static list = (params: ListRequest): Promise<Paginated<ConversationDto[]>> =>
    httpClient
      .get(Endpoint.conversation.list, {
        params,
      })
      .then(ApiMapper.toResponseData);

  static get = (conversationId: string): Promise<ConversationDto> =>
    httpClient
      .get(generatePath(Endpoint.conversation.get, { conversationId }))
      .then(ApiMapper.toResponseData);

  static getEvents = (conversationId: string, params: any) =>
    httpClient.get(generatePath(Endpoint.conversation.details, { conversationId }), {
      params,
    });

  static update = (conversationId: string, data: DeepPartial<ConversationDto>) =>
    httpClient
      .patch<ConversationDto>(
        `${generatePath(Endpoint.conversation.update, { conversationId })}/`,
        data,
      )
      .then(ApiMapper.toResponseData);

  static updateVisitor = (
    visitor: Partial<UpdateVisitorRequest>,
    conversationId: string,
  ): Promise<void> =>
    httpClient.patch(generatePath(Endpoint.conversation.visitor, { conversationId }), visitor);

  static reply = (data: Pick<Message, 'content'>, conversationId: string): Promise<unknown> =>
    httpClient.post(`${generatePath(Endpoint.conversation.reply, { conversationId })}/`, data);

  static getWhyAISaidThat = (
    conversationId: string,
    messageId: string,
  ): Promise<WhyAISaidThatDto | null> =>
    httpClient
      .get(generatePath(Endpoint.conversation.whyAISaidThat, { conversationId, messageId }))
      .then(ApiMapper.toResponseData)
      .catch((error: any) => {
        if (error?.response?.status === 404) {
          return null;
        }
        throw error;
      });

  static scheduleWhyAISaidThat = (
    conversationId: string,
    messageId: string,
  ): Promise<WhyAISaidThatDto | WhyAISaidThatScheduledDto> =>
    httpClient
      .post(generatePath(Endpoint.conversation.whyAISaidThat, { conversationId, messageId }))
      .then(ApiMapper.toResponseData);

  static translate = (conversationId: string, data: ConversationTranslateRequest) =>
    httpClient
      .post(generatePath(Endpoint.conversation.translate, { conversationId }), data)
      .then(ApiMapper.toResponseData);

  static rating = (conversationId: string, language: string): Promise<RatingDto[]> =>
    httpClient
      .get(generatePath(Endpoint.conversation.rating, { conversationId }), { params: { language } })
      .then(ApiMapper.toResponseData);

  static generateExperiment = (
    conversationId: string,
    experimentType: ConversationExperimentEnum,
  ) =>
    httpClient
      .post(
        generatePath(Endpoint.conversation.generateExperiment, { conversationId, experimentType }),
      )
      .then(ApiMapper.toResponseData);
}
